import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LanguageToggle } from '@/components/LanguageToggle';
import { toast } from 'sonner';
import { FilmSlate, Eye, EyeSlash } from '@phosphor-icons/react';

export default function RegisterPage() {
  const { register } = useAuth();
  const { t, language } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error(language === 'es' ? 'Por favor completa todos los campos' : 'Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success(language === 'es' ? '¡Cuenta creada exitosamente!' : 'Account created successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#171717] items-center justify-center p-12 relative">
        <div className="absolute top-6 right-6">
          <LanguageToggle />
        </div>
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#FF5D01] rounded-lg flex items-center justify-center">
              <FilmSlate size={28} weight="fill" className="text-white" />
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">Script Visual</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            {language === 'es' ? 'Comienza a crear historias visuales hoy' : 'Start creating visual stories today'}
          </h1>
          <p className="text-[#A1A1AA] text-lg">
            {language === 'es' 
              ? 'Únete a los creadores que usan Script Visual para transformar sus guiones de YouTube en storyboards profesionales.'
              : 'Join creators who use Script Visual to transform their YouTube scripts into professional storyboards.'}
          </p>
          <div className="mt-12 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#FF5D01] flex items-center justify-center text-white text-sm font-bold mt-0.5">1</div>
              <div>
                <div className="text-white font-medium">{language === 'es' ? 'Sube tu guión' : 'Upload your script'}</div>
                <div className="text-[#A1A1AA] text-sm">{language === 'es' ? 'Soporta TXT, DOCX, PDF o pega directamente' : 'Support for TXT, DOCX, PDF or paste directly'}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#FF5D01] flex items-center justify-center text-white text-sm font-bold mt-0.5">2</div>
              <div>
                <div className="text-white font-medium">{language === 'es' ? 'Auto-genera escenas' : 'Auto-generate scenes'}</div>
                <div className="text-[#A1A1AA] text-sm">{language === 'es' ? 'La IA divide tu guión en escenas visuales' : 'AI breaks your script into visual beats'}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#FF5D01] flex items-center justify-center text-white text-sm font-bold mt-0.5">3</div>
              <div>
                <div className="text-white font-medium">{language === 'es' ? 'Exporta tu storyboard' : 'Export your storyboard'}</div>
                <div className="text-[#A1A1AA] text-sm">{language === 'es' ? 'Descarga como PDF, CSV o paquete completo' : 'Download as PDF, CSV, or complete asset bundle'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6 lg:hidden">
          <LanguageToggle />
        </div>
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-[#FF5D01] rounded-lg flex items-center justify-center">
              <FilmSlate size={24} weight="fill" className="text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Script Visual</span>
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">{t('createYourAccount')}</h2>
          <p className="text-[#A1A1AA] mb-8">{language === 'es' ? 'Comienza a crear storyboards visuales en minutos' : 'Start building visual storyboards in minutes'}</p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#F8FAFC]">{language === 'es' ? 'Nombre Completo' : 'Full Name'}</Label>
              <Input
                id="name"
                type="text"
                placeholder={language === 'es' ? 'Juan Pérez' : 'John Doe'}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-[#171717] border-[#333333] text-white placeholder:text-[#A1A1AA] focus:ring-[#FF5D01] focus:border-[#FF5D01]"
                data-testid="register-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#F8FAFC]">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#171717] border-[#333333] text-white placeholder:text-[#A1A1AA] focus:ring-[#FF5D01] focus:border-[#FF5D01]"
                data-testid="register-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#F8FAFC]">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={language === 'es' ? 'Al menos 6 caracteres' : 'At least 6 characters'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#171717] border-[#333333] text-white placeholder:text-[#A1A1AA] focus:ring-[#FF5D01] focus:border-[#FF5D01] pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF5D01] hover:bg-[#e65400] text-white font-medium py-2.5 rounded-md transition-colors"
              data-testid="register-submit-btn"
            >
              {loading ? (language === 'es' ? 'Creando cuenta...' : 'Creating account...') : (language === 'es' ? 'Crear cuenta' : 'Create account')}
            </Button>
          </form>
          
          <p className="mt-6 text-center text-[#A1A1AA]">
            {t('hasAccount')}{' '}
            <Link to="/login" className="text-[#FF5D01] hover:underline font-medium" data-testid="go-to-login-link">
              {t('signInHere')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
